import { StageSelect } from "@/components/admin/stage-select";
import { SectionHeader } from "@/components/dashboard/section-header";
import { InlineError } from "@/components/dashboard/state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type ImportResult, adminApi } from "@/lib/admin-api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";

export function AdminCandidatesPage() {
  const qc = useQueryClient();
  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["admin", "candidates"],
    queryFn: () => adminApi.candidates.list(),
  });
  const { data: searches = [] } = useQuery({
    queryKey: ["admin", "searches"],
    queryFn: () => adminApi.searches.list(),
  });

  // quick-add
  const [name, setName] = useState("");
  const [searchId, setSearchId] = useState("");
  const [stage, setStage] = useState<string>("longlist");
  const quickAdd = useMutation({
    mutationFn: () => adminApi.candidates.quickAdd({ fullName: name.trim(), searchId, stage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "candidates"] });
      setName("");
      setSearchId("");
    },
  });

  // CSV import
  const fileRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const importM = useMutation({
    mutationFn: (file: File) => adminApi.importCsv(file),
    onSuccess: (res) => {
      setImportResult(res);
      qc.invalidateQueries({ queryKey: ["admin", "candidates"] });
    },
  });

  return (
    <div className="space-y-5">
      <SectionHeader title="Candidates" />

      <InlineError error={quickAdd.error ?? importM.error} />

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border bg-card p-4">
          <div className="eyebrow mb-2">Quick add</div>
          <div className="flex flex-wrap items-end gap-2">
            <Input className="w-44" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
            <select
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
            >
              <option value="">Select search…</option>
              {searches.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
            <StageSelect value={stage} onChange={setStage} />
            <Button
              size="sm"
              disabled={!name.trim() || !searchId || quickAdd.isPending}
              onClick={() => quickAdd.mutate()}
            >
              Add
            </Button>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-4">
          <div className="eyebrow mb-2">CSV import</div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importM.mutate(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
          <Button size="sm" variant="outline" disabled={importM.isPending} onClick={() => fileRef.current?.click()}>
            {importM.isPending ? "Importing…" : "Upload CSV"}
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Columns: full_name, email, current_employer, current_title, salary_expectation, notice_period_days, source,
            search_title, stage
          </p>
          {importResult ? (
            <p className="mt-2 text-xs">
              {importResult.created} created · {importResult.skipped} skipped (of {importResult.total})
            </p>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr className="text-left">
              {["Name", "Employer", "Title", "Location"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : candidates.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                  No candidates yet.
                </td>
              </tr>
            ) : (
              candidates.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-accent/50">
                  <td className="px-3 py-2 font-medium">
                    <Link
                      to="/admin/candidates/$candidateId"
                      params={{ candidateId: c.id }}
                      className="hover:underline"
                    >
                      {c.fullName}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{c.currentEmployer ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.currentTitle ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.location ?? "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
